import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink, faSpinner, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';

const SystemMaintenance: React.FC = () => {
  const [relinking, setRelinking] = useState(false);
  const [relinkResult, setRelinkResult] = useState<{
    success: boolean;
    articlesUpdated: number;
    articlesNotMatched: number;
    errors?: Array<{ articleNumber: string; creditorNumber: string; error: string }>;
  } | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [articleStats, setArticleStats] = useState<{
    total: number;
    withCreditorNumber: number;
    withoutCreditorNumber: number;
    withSupplierId: number;
    withoutSupplierId: number;
    creditorCoverage: number;
    supplierCoverage: number;
  } | null>(null);

  const handleLoadStats = async () => {
    setLoadingStats(true);
    try {
      const response = await api.get('/suppliers/maintenance/article-stats');
      setArticleStats(response.data.statistics);
    } catch (error: any) {
      console.error('Error loading article stats:', error);
      alert('Fehler beim Laden der Statistiken: ' + (error?.response?.data?.message || error?.message));
    } finally {
      setLoadingStats(false);
    }
  };

  const handleRelinkArticles = async () => {
    if (!confirm('Möchten Sie alle Artikel mit ihren Lieferanten neu verknüpfen? Dies kann einige Sekunden dauern.')) {
      return;
    }

    setRelinking(true);
    setRelinkResult(null);

    try {
      const response = await api.post('/suppliers/maintenance/relink-articles');
      
      setRelinkResult({
        success: true,
        articlesUpdated: response.data.result.articlesUpdated,
        articlesNotMatched: response.data.result.articlesNotMatched,
        errors: response.data.result.errors
      });
    } catch (error: any) {
      console.error('Error relinking articles:', error);
      setRelinkResult({
        success: false,
        articlesUpdated: 0,
        articlesNotMatched: 0,
        errors: [{ articleNumber: 'N/A', creditorNumber: 'N/A', error: error?.response?.data?.message || error?.message || 'Unbekannter Fehler' }]
      });
    } finally {
      setRelinking(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          <FontAwesomeIcon icon={faLink} className="mr-2 text-blue-600" />
          System Wartung
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Verwenden Sie diese Tools, um Wartungsaufgaben durchzuführen und die Integrität der Daten zu gewährleisten.
        </p>

        {/* Artikel-Verknüpfung */}
        <div className="border border-gray-200 rounded-lg p-4 mb-4">
          <h4 className="text-base font-medium text-gray-900 mb-2">
            Artikel-Verknüpfung aktualisieren
          </h4>
          <p className="text-sm text-gray-600 mb-4">
            Diese Funktion verknüpft alle Artikel mit ihren zugehörigen Lieferanten basierend auf der Kreditor-Nummer.
            <br />
            <strong>Wann verwenden:</strong> Nach dem Neuimport von Lieferanten oder wenn Artikel ihre Lieferanten-Zuordnung verloren haben.
          </p>

          <button
            onClick={handleRelinkArticles}
            disabled={relinking}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center"
          >
            {relinking ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                Verknüpfe Artikel...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faLink} className="mr-2" />
                Artikel neu verknüpfen
              </>
            )}
          </button>

          {/* Result Display */}
          {relinkResult && (
            <div className={`mt-4 p-4 rounded-lg ${relinkResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              {relinkResult.success ? (
                <>
                  <div className="flex items-center mb-2">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mr-2" />
                    <h5 className="font-medium text-green-900">Verknüpfung erfolgreich abgeschlossen</h5>
                  </div>
                  <div className="text-sm text-green-800 space-y-1">
                    <p>✅ <strong>{relinkResult.articlesUpdated}</strong> Artikel wurden neu verknüpft</p>
                    {relinkResult.articlesNotMatched > 0 && (
                      <p className="text-orange-700">
                        ⚠️ <strong>{relinkResult.articlesNotMatched}</strong> Artikel konnten nicht zugeordnet werden (kein passender Lieferant gefunden)
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center mb-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mr-2" />
                    <h5 className="font-medium text-red-900">Fehler bei der Verknüpfung</h5>
                  </div>
                  {relinkResult.errors && relinkResult.errors.length > 0 && (
                    <div className="text-sm text-red-800 mt-2">
                      <p>{relinkResult.errors[0].error}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Artikel-Statistiken */}
        <div className="border border-gray-200 rounded-lg p-4 mb-4">
          <h4 className="text-base font-medium text-gray-900 mb-2">
            Artikel-Verknüpfungs-Diagnose
          </h4>
          <p className="text-sm text-gray-600 mb-4">
            Prüfen Sie, wie viele Artikel korrekt mit Lieferanten verknüpft sind.
          </p>

          <button
            onClick={handleLoadStats}
            disabled={loadingStats}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center mb-4"
          >
            {loadingStats ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                Lade Statistiken...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                Statistiken laden
              </>
            )}
          </button>

          {/* Stats Display */}
          {articleStats && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h5 className="font-medium text-gray-900 mb-3">📊 Artikel-Übersicht</h5>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Gesamt Artikel:</p>
                  <p className="text-lg font-semibold text-gray-900">{articleStats.total.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-600">Mit Kreditor-Nr:</p>
                  <p className="text-lg font-semibold text-green-600">
                    {articleStats.withCreditorNumber.toLocaleString()} ({articleStats.creditorCoverage}%)
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Ohne Kreditor-Nr:</p>
                  <p className={`text-lg font-semibold ${articleStats.withoutCreditorNumber > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                    {articleStats.withoutCreditorNumber.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Mit Lieferant:</p>
                  <p className="text-lg font-semibold text-green-600">
                    {articleStats.withSupplierId.toLocaleString()} ({articleStats.supplierCoverage}%)
                  </p>
                </div>
              </div>
              
              {articleStats.withoutCreditorNumber > 0 && (
                <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded text-sm text-orange-800">
                  ⚠️ <strong>{articleStats.withoutCreditorNumber}</strong> Artikel haben keine Kreditor-Nummer und können nicht automatisch zugeordnet werden.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h5 className="text-sm font-medium text-blue-900 mb-2">
            💡 Hinweis zum Lieferanten-Import
          </h5>
          <p className="text-sm text-blue-800">
            Nach jedem Lieferanten-Import werden die Artikel automatisch neu verknüpft. 
            Diese Funktion ist nur für manuelle Korrekturen notwendig.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SystemMaintenance;
